import React from 'react';
import { Link } from 'react-router-dom';
import { useHOA } from '../contexts/HOAContext';
import { hoaProfile } from '../lib/hoaProfile';
import { Mountain, MapPin, Home, ChevronLeft, Phone, Mail, ExternalLink } from 'lucide-react';

const MapPage = () => {
    const profile = hoaProfile(useHOA().hoa);
    const mc = profile.managementCompany;
    return (
        <div className="min-h-screen bg-cream">
            {/* Header */}
            <header className="bg-gradient-to-r from-forest-900 to-forest-800 text-white">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group">
                        <ChevronLeft className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                        <Mountain className="w-8 h-8" />
                        <div>
                            <h1 className="font-display text-xl font-bold leading-tight">{profile.shortName}</h1>
                            <p className="text-forest-200 text-xs">{profile.location}</p>
                        </div>
                    </Link>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                {/* Page Title */}
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-forest-100">
                        <MapPin className="w-5 h-5 text-forest-700" />
                    </div>
                    <h2 className="font-display font-bold text-slate-900 text-2xl">Community Map</h2>
                </div>

                {/* Map + Info Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Map Embed */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                        <iframe
                            title={`${profile.shortName} Community Map`}
                            src={`https://maps.google.com/maps?q=${encodeURIComponent(profile.mapQuery || profile.name)}&output=embed`}
                            width="100%"
                            height="500"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="w-full"
                        />
                    </div>

                    {/* Sidebar: Community Info + Management */}
                    <div className="space-y-6">
                        {/* Community Info */}
                        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="p-2 rounded-lg bg-forest-100">
                                    <Home className="w-5 h-5 text-forest-700" />
                                </div>
                                <h3 className="font-display font-bold text-slate-900 text-lg">Community Info</h3>
                            </div>
                            <div className="space-y-4">
                                <InfoRow label="Association" value={profile.legalName} />
                                <InfoRow label="Location" value={profile.locationLong} />
                            </div>
                        </div>

                        {/* Management Company (only when the association has one on file) */}
                        {mc && (
                        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="p-2 rounded-lg bg-forest-100">
                                    <Phone className="w-5 h-5 text-forest-700" />
                                </div>
                                <h3 className="font-display font-bold text-slate-900 text-lg">Property Management</h3>
                            </div>
                            <p className="text-slate-800 font-semibold text-sm mb-4">{mc.name}</p>
                            <div className="space-y-3">
                                {mc.phone && (
                                <div className="flex items-center gap-2.5">
                                    <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <a href={`tel:${mc.phone.replace(/[^0-9+]/g, '')}`} className="text-sm text-slate-600 hover:text-forest-700 transition-colors">{mc.phone}</a>
                                </div>
                                )}
                                {mc.email && (
                                <div className="flex items-center gap-2.5">
                                    <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <a href={`mailto:${mc.email}`} className="text-sm text-slate-600 hover:text-forest-700 transition-colors">{mc.email}</a>
                                </div>
                                )}
                                {mc.website && (
                                <div className="flex items-center gap-2.5">
                                    <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <a href={mc.website} target="_blank" rel="noopener noreferrer" className="text-sm text-forest-700 hover:text-forest-600 font-medium transition-colors">Website</a>
                                </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-start gap-3">
        <span className="text-sm text-slate-500 font-medium flex-shrink-0">{label}</span>
        <span className="text-sm text-slate-800 font-medium text-right">{value}</span>
    </div>
);

export default MapPage;
